import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '../../../services/notification.service';
import { ShipmentRequestService, ClientsBrands, ClientFieldSettings, DealerStatus, Dealer } from '../../../services/shipment-request.service';

export type DealerPopupMode = 'create' | 'edit';

type DealerForm = Omit<Dealer, 'dealerOperatingPhone' | 'dealerOperatingEmail'>;

@Component({
  selector: 'app-dealer-management-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './dealer-management-popup.component.html',
  styleUrl: './dealer-management-popup.component.css'
})
export class DealerManagementPopupComponent implements OnChanges {

  @Input() visible: boolean = false;
  @Input() mode: DealerPopupMode = 'create';
  @Input() dealer: Dealer | null = null;
  @Input() defaultClientCode: string = '';
  @Input() defaultBrandCode: string = '';
  @Input() clientsBrands: ClientsBrands[] = [];
  @Input() fieldSettings: ClientFieldSettings | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Dealer>();

  // ===== Form model =====
  form: DealerForm = this.emptyDealer();
  availableBrands: { brandCode: string; brandName: string }[] = [];
  submitting = false;
  errorMessage = '';

  constructor(
    private shipmentRequestService: ShipmentRequestService,
    private notificationService: NotificationService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.initForm();
    }
  }

  // ===== Init / reset =====
  private initForm(): void {
    this.errorMessage = '';
    this.submitting = false;

    if (this.mode === 'edit' && this.dealer) {
      this.form = { ...this.dealer };
    } else {
      this.form = this.emptyDealer();
      if (this.defaultClientCode) this.form.dealerClientCode = this.defaultClientCode;
      if (this.defaultBrandCode) this.form.dealerBrandCode = this.defaultBrandCode;
    }

    // Paese sempre IT, sia in create che in edit
    this.form.dealerOperatingAddressCountry = 'IT';

    this.refreshAvailableBrands();
  }

  private emptyDealer(): DealerForm {
    return {
      dealerCode: '',
      dealerClientCode: '',
      dealerBrandCode: '',
      dealerName: '',
      dealerVatNumber: '',
      dealerOperatingAddressStreet: '',
      dealerOperatingAddressNumber: '',
      dealerOperatingAddressZip: '',
      dealerOperatingAddressCity: '',
      dealerOperatingAddressProvince: '',
      dealerOperatingAddressCountry: 'IT',
      dealerStatus: DealerStatus.ACTIVE,
      dealerVersion: 0,
      dealerCreatedAt: new Date().toISOString()
    };
  }

  /**
   * Forza il valore della Ragione Sociale in maiuscolo mentre l'utente digita,
   * preservando la posizione del cursore.
   */
  onDealerNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upper = input.value.toUpperCase();

    this.form.dealerName = upper;
    input.value = upper;
    input.setSelectionRange(start, end);
  }

  // ===== Client/brand handling =====

  get filteredClientsBrands(): ClientsBrands[] {
    if (!this.defaultClientCode) return this.clientsBrands;
    return this.clientsBrands.filter(c => c.clientCode === this.defaultClientCode);
  }

  onClientChange(): void {
    this.form.dealerBrandCode = '';
    this.refreshAvailableBrands();
  }

  private refreshAvailableBrands(): void {
    const client = this.clientsBrands.find(cb => cb.clientCode === this.form.dealerClientCode);
    this.availableBrands = (client?.brands ?? [])
      .map(b => ({ brandCode: b.brandCode, brandName: b.brandName ?? b.brandCode }))
      .sort((a, b) => a.brandName.localeCompare(b.brandName));

    // Auto-seleziona se uno solo
    if (this.form.dealerClientCode && this.availableBrands.length === 1) {
      this.form.dealerBrandCode = this.availableBrands[0].brandCode;
    }
  }

  // ===== Validazione minima =====
  private isFormValid(): boolean {
    if (this.dealerCodeRequired && !this.form.dealerCode?.trim()) return false;
    if (!this.form.dealerName?.trim()) return false;
    if (!this.form.dealerOperatingAddressStreet?.trim()) return false;
    if (!this.form.dealerOperatingAddressZip?.trim()) return false;
    if (!this.form.dealerOperatingAddressCity?.trim()) return false;
    if (!this.form.dealerOperatingAddressProvince?.trim()) return false;
    if (this.mode === 'create') {
      if (!this.form.dealerClientCode) return false;
      if (!this.form.dealerBrandCode) return false;
    }
    return true;
  }

  // ===== Azioni =====
  onClose(): void {
    if (this.submitting) return;
    this.closed.emit();
  }

  onSubmit(): void {
    this.errorMessage = '';

    if (!this.isFormValid()) {
      this.errorMessage = 'Compila tutti i campi obbligatori';
      return;
    }

    this.submitting = true;

    const request$ = this.mode === 'create'
      ? this.shipmentRequestService.createDealer(this.form)
      : this.shipmentRequestService.updateDealer(this.form);

    request$.subscribe({
      next: (savedDealer) => {
        this.submitting = false;
        this.notificationService.success(
          this.mode === 'create'
            ? 'Dealer creato con successo'
            : 'Dealer aggiornato con successo'
        );
        this.saved.emit(savedDealer);
      },
      error: (err) => {
        this.submitting = false;
        this.errorMessage = err?.error?.message || err?.message || 'Errore durante il salvataggio';
        this.notificationService.error(this.errorMessage);
      }
    });
  }

  get title(): string {
    return this.mode === 'create' ? 'Nuovo Dealer' : 'Modifica Dealer';
  }

  get submitLabel(): string {
    return this.mode === 'create' ? 'Crea Dealer' : 'Salva Modifiche';
  }

  // ===== dealerCode field settings (solo create) =====
  get dealerCodeEnabled(): boolean {
    if (this.mode === 'edit') return false;
    const cfg = this.fieldSettings?.fields?.['dealerCode'];
    return cfg?.enabled !== false;
  }

  get dealerCodeRequired(): boolean {
    if (this.mode === 'edit') return false;
    const cfg = this.fieldSettings?.fields?.['dealerCode'];
    return cfg?.required === true;
  }

  get dealerCodePlaceholder(): string {
    const cfg = this.fieldSettings?.fields?.['dealerCode'];
    if (cfg?.placeholder) return cfg.placeholder;
    return this.dealerCodeEnabled ? 'Inserisci codice destinatario' : 'Generato automaticamente';
  }
}
